import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  TextField,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationDocument,
  GetSubscriptionsDocument,
  GetAccountingDocumentsDocument,
  GetPaymentsDocument,
  GetPaymentAccountsDocument,
  GetLicencesDocument,
  GetTenantDocument,
  UpdateTenantDiscountPercentDocument,
  type GetOrganizationQuery,
  type GetSubscriptionsQuery,
  type GetAccountingDocumentsQuery,
  type GetPaymentsQuery,
  type GetPaymentAccountsQuery,
  type GetLicencesQuery,
} from '@/generated/root-types';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { DataTable } from '@gastrosoft/shared/ui';
import {
  SubscriptionState,
  AccountingDocumentState,
  AccountingDocumentType,
  PaymentState,
} from '@/types/billing';
import {
  accountingDocumentStateColors,
  licenceStateColors,
  paymentStateColors,
  subscriptionStateColors,
  translateAccountingDocumentState,
  translateLicenceState,
  translatePaymentDirection,
  translatePaymentState,
  translateSubscriptionState,
} from '@/utils/billingStateLabels';
import { translateAddressType } from '@/utils/addressLabels';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ height: '100%' }}>
      {value === index && <Box sx={{ py: 2, height: '100%' }}>{children}</Box>}
    </div>
  );
}

export default function TenantDetail() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);
  const { id: organizationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [discountPercent, setDiscountPercent] = useState('');

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount?: number | null, currency?: string | null) => {
    if (amount == null) return '–';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
      amount,
    );
  };
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const docTypeLabels: Record<string, string> = {
    INVOICE: t('invoices.docTypeInvoice'),
    CREDIT_NOTE: t('invoices.docTypeCreditNote'),
    DEBIT_NOTE: t('invoices.docTypeDebitNote'),
    RECEIPT: t('invoices.docTypeReceipt'),
  };

  const { data: orgData, loading: orgLoading } = useQuery<GetOrganizationQuery>(
    GetOrganizationDocument,
    {
      variables: { id: organizationId },
      skip: !organizationId,
    },
  );

  const organization = orgData?.organization;
  const tenantId = organization?.tenantId ?? null;

  const { data: tenantData, refetch: refetchTenant } = useQuery(GetTenantDocument, {
    variables: { id: tenantId! },
    skip: !tenantId || !superAdmin,
  });

  const resellerTenant = tenantData?.tenant;
  const isResellerTenant = resellerTenant?.type === 'RESELLER';

  useEffect(() => {
    if (resellerTenant?.discountPercent != null) {
      setDiscountPercent(String(resellerTenant.discountPercent));
    }
  }, [resellerTenant?.discountPercent]);

  const [updateDiscount, { loading: savingDiscount }] = useMutation(
    UpdateTenantDiscountPercentDocument,
  );

  const handleSaveDiscount = async () => {
    if (!tenantId) return;
    const parsed = Number.parseFloat(discountPercent);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      showError(t('tenantDetail.discountInvalid'));
      return;
    }
    try {
      await updateDiscount({
        variables: { input: { tenantId, discountPercent: parsed } },
      });
      showSuccess(t('tenantDetail.discountSaved'));
      void refetchTenant();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    if (tenantId) setOverrideTenantId(tenantId);
    return () => setOverrideTenantId(null);
  }, [tenantId]);

  const queryVars = { first: 50, tenantId, order: [{ createdAt: 'DESC' as const }] };

  const { data: subsData, loading: subsLoading } = useQuery<GetSubscriptionsQuery>(
    GetSubscriptionsDocument,
    {
      variables: queryVars,
      skip: !tenantId,
    },
  );

  const { data: docsData, loading: docsLoading } = useQuery<GetAccountingDocumentsQuery>(
    GetAccountingDocumentsDocument,
    {
      variables: queryVars,
      skip: !tenantId,
    },
  );

  const { data: paymentsData, loading: paymentsLoading } = useQuery<GetPaymentsQuery>(
    GetPaymentsDocument,
    {
      variables: queryVars,
      skip: !tenantId,
    },
  );

  const { data: activeSubsCountData } = useQuery<GetSubscriptionsQuery>(GetSubscriptionsDocument, {
    variables: {
      first: 1,
      tenantId,
      where: { state: { eq: SubscriptionState.ACTIVE } },
    },
    skip: !tenantId,
  });
  const { data: pendingSubsCountData } = useQuery<GetSubscriptionsQuery>(GetSubscriptionsDocument, {
    variables: {
      first: 1,
      tenantId,
      where: { state: { eq: SubscriptionState.PENDING } },
    },
    skip: !tenantId,
  });
  const { data: cancelledSubsCountData } = useQuery<GetSubscriptionsQuery>(
    GetSubscriptionsDocument,
    {
      variables: {
        first: 1,
        tenantId,
        where: { state: { eq: SubscriptionState.CANCELLED } },
      },
      skip: !tenantId,
    },
  );
  const { data: expiredSubsCountData } = useQuery<GetSubscriptionsQuery>(GetSubscriptionsDocument, {
    variables: {
      first: 1,
      tenantId,
      where: { state: { eq: SubscriptionState.EXPIRED } },
    },
    skip: !tenantId,
  });
  const { data: suspendedSubsCountData } = useQuery<GetSubscriptionsQuery>(
    GetSubscriptionsDocument,
    {
      variables: {
        first: 1,
        tenantId,
        where: { state: { eq: SubscriptionState.SUSPENDED } },
      },
      skip: !tenantId,
    },
  );

  const { data: openDocsCountData } = useQuery<GetAccountingDocumentsQuery>(
    GetAccountingDocumentsDocument,
    {
      variables: {
        first: 1,
        tenantId,
        where: {
          state: { in: [AccountingDocumentState.SENT, AccountingDocumentState.OVERDUE] },
        },
      },
      skip: !tenantId,
    },
  );
  const { data: overdueDocsCountData } = useQuery<GetAccountingDocumentsQuery>(
    GetAccountingDocumentsDocument,
    {
      variables: {
        first: 1,
        tenantId,
        where: { state: { eq: AccountingDocumentState.OVERDUE } },
      },
      skip: !tenantId,
    },
  );
  const { data: draftDocsCountData } = useQuery<GetAccountingDocumentsQuery>(
    GetAccountingDocumentsDocument,
    {
      variables: {
        first: 1,
        tenantId,
        where: { state: { eq: AccountingDocumentState.DRAFT } },
      },
      skip: !tenantId,
    },
  );
  const { data: creditNotesCountData } = useQuery<GetAccountingDocumentsQuery>(
    GetAccountingDocumentsDocument,
    {
      variables: {
        first: 1,
        tenantId,
        where: { type: { eq: AccountingDocumentType.CREDIT_NOTE } },
      },
      skip: !tenantId,
    },
  );
  const { data: paidInvoicesData } = useQuery<GetAccountingDocumentsQuery>(
    GetAccountingDocumentsDocument,
    {
      variables: {
        first: 50,
        tenantId,
        where: {
          type: { eq: AccountingDocumentType.INVOICE },
          state: { eq: AccountingDocumentState.PAID },
        },
      },
      skip: !tenantId,
    },
  );
  const { data: openInvoicesData } = useQuery<GetAccountingDocumentsQuery>(
    GetAccountingDocumentsDocument,
    {
      variables: {
        first: 50,
        tenantId,
        where: {
          type: { eq: AccountingDocumentType.INVOICE },
          state: { in: [AccountingDocumentState.SENT, AccountingDocumentState.OVERDUE] },
        },
      },
      skip: !tenantId,
    },
  );

  const { data: completedPaymentsCountData } = useQuery<GetPaymentsQuery>(GetPaymentsDocument, {
    variables: {
      first: 1,
      tenantId,
      where: { state: { eq: PaymentState.COMPLETED } },
    },
    skip: !tenantId,
  });

  const { data: payAccData, loading: payAccLoading } = useQuery<GetPaymentAccountsQuery>(
    GetPaymentAccountsDocument,
    {
      variables: { first: 50, tenantId },
      skip: !tenantId,
    },
  );

  const { data: licencesData, loading: licencesLoading } = useQuery<GetLicencesQuery>(
    GetLicencesDocument,
    {
      variables: {
        first: 50,
        tenantId: tenantId ?? undefined,
        organizationId: organizationId ?? undefined,
      },
      skip: !tenantId || !organizationId,
    },
  );

  const subscriptions = subsData?.subscriptions?.nodes ?? [];
  const documents = docsData?.accountingDocuments?.nodes ?? [];
  const payments = paymentsData?.payments?.nodes ?? [];
  const units = organization?.units ?? [];
  const addresses = organization?.addresses ?? [];
  const paymentAccounts = payAccData?.paymentAccounts?.nodes ?? [];
  const licences = licencesData?.licences?.nodes ?? [];

  const activeSubscriptionsCount = activeSubsCountData?.subscriptions?.totalCount ?? 0;
  const subscriptionStateCounts: Record<string, number> = {
    [SubscriptionState.ACTIVE]: activeSubscriptionsCount,
    [SubscriptionState.PENDING]: pendingSubsCountData?.subscriptions?.totalCount ?? 0,
    [SubscriptionState.CANCELLED]: cancelledSubsCountData?.subscriptions?.totalCount ?? 0,
    [SubscriptionState.EXPIRED]: expiredSubsCountData?.subscriptions?.totalCount ?? 0,
    [SubscriptionState.SUSPENDED]: suspendedSubsCountData?.subscriptions?.totalCount ?? 0,
  };
  const openDocsCount = openDocsCountData?.accountingDocuments?.totalCount ?? 0;
  const overdueDocsCount = overdueDocsCountData?.accountingDocuments?.totalCount ?? 0;
  const draftDocsCount = draftDocsCountData?.accountingDocuments?.totalCount ?? 0;
  const creditNotesCount = creditNotesCountData?.accountingDocuments?.totalCount ?? 0;
  const completedPaymentsCount = completedPaymentsCountData?.payments?.totalCount ?? 0;
  const paidRevenue = (paidInvoicesData?.accountingDocuments?.nodes ?? []).reduce(
    (sum, d) => sum + d.amountGross,
    0,
  );
  const openRevenue = (openInvoicesData?.accountingDocuments?.nodes ?? []).reduce(
    (sum, d) => sum + d.amountGross,
    0,
  );
  const listParams = new URLSearchParams();
  if (organization?.tenantId) listParams.set('tenantId', organization.tenantId);
  if (organization?.id) listParams.set('organizationId', organization.id);
  const filterQs = listParams.toString() ? `?${listParams.toString()}` : '';

  const subscriptionColumns: GridColDef[] = [
    { field: 'number', headerName: t('common.number'), width: 120 },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={translateSubscriptionState(t, String(params.value))}
          size="small"
          color={subscriptionStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    {
      field: 'organizationUnit',
      headerName: t('common.orgUnit'),
      flex: 1,
      valueGetter: (value: unknown) => (value as { name?: string })?.name ?? '–',
    },
    {
      field: 'amountGross',
      headerName: t('common.amount'),
      width: 100,
      valueFormatter: (value: number) => formatCurrency(value),
    },
    {
      field: 'intervalMonths',
      headerName: t('common.interval'),
      width: 80,
      valueFormatter: (value: number) => (value ? `${value} ${t('subscriptions.months')}` : '–'),
    },
    {
      field: 'activationDate',
      headerName: t('common.activated'),
      width: 100,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'currentTermEndDate',
      headerName: t('common.termUntil'),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  const documentColumns: GridColDef[] = [
    { field: 'number', headerName: t('common.number'), width: 130 },
    {
      field: 'type',
      headerName: t('common.type'),
      width: 100,
      valueFormatter: (value: string) => docTypeLabels[value] || value,
    },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={translateAccountingDocumentState(t, String(params.value))}
          size="small"
          color={accountingDocumentStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    {
      field: 'amountGross',
      headerName: t('common.gross'),
      width: 100,
      valueFormatter: (value: number) => formatCurrency(value),
    },
    {
      field: 'taxAmount',
      headerName: t('common.vat'),
      width: 80,
      valueFormatter: (value: number) => formatCurrency(value),
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 100,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'sentAt',
      headerName: t('common.sent'),
      width: 100,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  const paymentColumns: GridColDef[] = [
    {
      field: 'accountingDocument',
      headerName: t('common.invoice'),
      width: 130,
      valueGetter: (value: unknown) => (value as { number?: string })?.number ?? '–',
    },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={translatePaymentState(t, String(params.value))}
          size="small"
          color={paymentStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    {
      field: 'direction',
      headerName: t('common.direction'),
      width: 110,
      valueFormatter: (value: string) => translatePaymentDirection(t, value),
    },
    {
      field: 'amountGross',
      headerName: t('common.amount'),
      width: 100,
      valueFormatter: (value: number) => formatCurrency(value),
    },
    {
      field: 'paymentGateway',
      headerName: t('common.gateway'),
      width: 120,
      valueGetter: (value: unknown) => (value as { name?: string })?.name ?? '–',
    },
    {
      field: 'paymentDate',
      headerName: t('common.paymentDate'),
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    },
    { field: 'reference', headerName: t('common.reference'), flex: 1 },
  ];

  const unitColumns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1 },
    {
      field: 'description',
      headerName: t('common.description'),
      flex: 1,
      valueGetter: (v: string) => v ?? '–',
    },
    { field: 'sort', headerName: 'Sort', width: 80 },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  type PaymentAccountRow = NonNullable<
    NonNullable<GetPaymentAccountsQuery['paymentAccounts']>['nodes']
  >[number];

  const paymentAccountColumns: GridColDef<PaymentAccountRow>[] = [
    {
      field: 'paymentGateway',
      headerName: t('common.gateway'),
      width: 200,
      valueGetter: (_v, row) => row.paymentGateway?.name ?? '–',
    },
    {
      field: 'gatewayActive',
      headerName: t('paymentAccountDetail.gatewayActive'),
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const g = params.row.paymentGateway;
        if (!g) return '–';
        return (
          <Chip
            label={g.isActive ? t('common.active') : t('paymentAccountDetail.inactive')}
            size="small"
            color={g.isActive ? 'success' : 'default'}
            variant={g.isActive ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      field: 'bankName',
      headerName: t('paymentAccountDetail.bankName'),
      width: 160,
      valueGetter: (_v, row) => row.paymentMethod?.bankName ?? '–',
    },
    {
      field: 'bankAccountNumberEnding',
      headerName: t('paymentAccountDetail.accountEnding'),
      width: 120,
      valueGetter: (_v, row) => row.paymentMethod?.ending ?? '–',
    },
    {
      field: 'organizationUnits',
      headerName: t('paymentAccountDetail.orgUnits'),
      width: 180,
      sortable: false,
      valueGetter: (_v, row) =>
        row.organizationUnits?.length ? row.organizationUnits.map((u) => u.name).join(', ') : '–',
    },
    {
      field: 'subscriptions',
      headerName: t('paymentAccountDetail.linkedSubscriptions'),
      minWidth: 160,
      sortable: false,
      valueGetter: (_v, row) =>
        row.subscriptions?.length ? row.subscriptions.map((s) => s.number).join(', ') : '–',
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  const licenceColumns: GridColDef[] = [
    { field: 'serial', headerName: t('common.serialNumber'), flex: 1, minWidth: 180 },
    { field: 'appName', headerName: t('common.app'), width: 130 },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={translateLicenceState(t, String(params.value))}
          size="small"
          color={licenceStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    { field: 'type', headerName: t('common.type'), width: 100 },
    {
      field: 'organizationUnit',
      headerName: t('licences.orgUnit'),
      width: 140,
      valueGetter: (value: unknown) => (value as { name?: string })?.name ?? '–',
    },
    {
      field: 'expirationDate',
      headerName: t('common.validUntil'),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  if (orgLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box sx={{ p: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customers')}>
          {t('common.back')}
        </Button>
        <Typography color="text.secondary">{t('errors.customerNotFound')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customers')}>
          {t('common.back')}
        </Button>
        <Typography variant="h4">{organization.name}</Typography>
        {organization.tenant?.resellerId != null && (
          <Chip
            label={`Reseller: ${organization.tenant.resellerId}`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label={t('tenantDetail.overview')} />
          <Tab label={`${t('tenantDetail.tabsSubscriptions')} (${subscriptions.length})`} />
          <Tab label={`${t('tenantDetail.tabsInvoices')} (${documents.length})`} />
          <Tab label={`${t('tenantDetail.tabsPayments')} (${payments.length})`} />
          <Tab label={`${t('tenantDetail.tabsOrgUnits')} (${units.length})`} />
          <Tab label={`${t('tenantDetail.tabsLicences')} (${licences.length})`} />
          <Tab label={t('tenantDetail.tabsUsers')} />
          <Tab label={`${t('tenantDetail.tabsPaymentAccounts')} (${paymentAccounts.length})`} />
        </Tabs>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <TabPanel value={tabIndex} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('tenantDetail.masterDataOrg')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('common.name')}
                    </Typography>
                    <Typography variant="body2">{organization.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenantDetail.statusType')}
                    </Typography>
                    <Typography variant="body2">
                      {organization.status ?? '–'}{' '}
                      {organization.type ? `· ${organization.type}` : ''}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('common.resellerId')}
                    </Typography>
                    <Typography variant="body2">
                      {organization.tenant?.resellerId ?? '–'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('customers.customerNumber')}
                    </Typography>
                    <Typography variant="body2">{organization.customerNumber ?? '–'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenantDetail.createdAt')}
                    </Typography>
                    <Typography variant="body2">{formatDate(organization.createdAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenantDetail.updatedAt')}
                    </Typography>
                    <Typography variant="body2">{formatDate(organization.updatedAt)}</Typography>
                  </Box>
                </Box>
                {organization.description && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {organization.description}
                  </Typography>
                )}
              </Paper>

              {superAdmin && isResellerTenant ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t('tenantDetail.provisionTitle')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                      gap: 2,
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <TextField
                      label={t('tenantDetail.discountPercent')}
                      type="number"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSaveDiscount}
                      disabled={savingDiscount}
                    >
                      {t('tenantDetail.saveDiscount')}
                    </Button>
                  </Box>
                </Paper>
              ) : null}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('tenantDetail.addresses')}
                  </Typography>
                  <Button
                    component={RouterLink}
                    to={`/organization-addresses${filterQs}`}
                    size="small"
                  >
                    {t('organizationDetail.manageAddresses')}
                  </Button>
                </Box>
                {addresses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('tenantDetail.noAddresses')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {addresses.map((a) => (
                      <Card
                        key={a.id}
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          transition: (theme) =>
                            theme.transitions.create(['background-color', 'box-shadow']),
                          '&:hover': { bgcolor: 'action.hover', boxShadow: 1 },
                        }}
                        onClick={() =>
                          navigate(`/organization-addresses/${a.id}`, {
                            state: {
                              addressesListSearch: filterQs,
                              tenantIdOverride: organization.tenantId,
                            },
                          })
                        }
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="subtitle2">
                              {translateAddressType(t, a.type)}
                            </Typography>
                            {a.isDefault ? (
                              <Chip
                                label={t('orgAddresses.standardAddress')}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : null}
                          </Box>
                          <Typography variant="body2" color="text.primary">
                            {[a.addressLine1, a.addressLine2].filter(Boolean).join(', ')}
                            {a.zipCode || a.city
                              ? ` · ${[a.zipCode, a.city].filter(Boolean).join(' ')}`
                              : ''}
                            {a.country ? ` · ${a.country}` : ''}
                          </Typography>
                          {(a.phoneNumber || a.email) && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              {[a.phoneNumber, a.email].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                          {a.organizationUnit?.name && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 0.25 }}
                            >
                              {t('common.orgUnit')}: {a.organizationUnit.name}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Paper>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 2,
                }}
              >
                <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setTabIndex(1)}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.subscriptionsTotal')}
                    </Typography>
                    <Typography variant="h4">{subscriptions.length}</Typography>
                    <Typography variant="caption" color="success.main">
                      {activeSubscriptionsCount} {t('common.active')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setTabIndex(2)}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.invoices')}
                    </Typography>
                    <Typography variant="h4">{documents.length}</Typography>
                    <Typography variant="caption" color="warning.main">
                      {openDocsCount} {t('common.open')}
                      {overdueDocsCount > 0 && (
                        <>
                          {' '}
                          · {overdueDocsCount} {t('common.overdue')}
                        </>
                      )}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setTabIndex(3)}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.payments')}
                    </Typography>
                    <Typography variant="h4">{payments.length}</Typography>
                    <Typography variant="caption" color="success.main">
                      {completedPaymentsCount} {t('common.completed')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setTabIndex(4)}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.orgUnits')}
                    </Typography>
                    <Typography variant="h4">{units.length}</Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/organization-addresses${filterQs}`)}
                >
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.addresses')}
                    </Typography>
                    <Typography variant="h4">{addresses.length}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('organizationDetail.manageAddresses')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setTabIndex(5)}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.licences')}
                    </Typography>
                    <Typography variant="h4">{licences.length}</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setTabIndex(6)}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.users')}
                    </Typography>
                    <Typography variant="h4">–</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('common.seeTab')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => setTabIndex(7)}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      {t('tenantDetail.paymentAccounts')}
                    </Typography>
                    <Typography variant="h4">{paymentAccounts.length}</Typography>
                  </CardContent>
                </Card>
              </Box>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('tenantDetail.revenueAccounting')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenantDetail.revenuePaid')}
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(paidRevenue)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenantDetail.openInvoicesGross')}
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {formatCurrency(openRevenue)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenantDetail.draftInvoices')}
                    </Typography>
                    <Typography variant="body1">{draftDocsCount}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenantDetail.creditNotes')}
                    </Typography>
                    <Typography variant="body1">{creditNotesCount}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('tenantDetail.subscriptionsByStatus')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {[
                    {
                      labelKey: 'dashboard.chartActive' as const,
                      state: SubscriptionState.ACTIVE,
                      color: 'success.main',
                    },
                    {
                      labelKey: 'dashboard.chartPending' as const,
                      state: SubscriptionState.PENDING,
                      color: 'warning.main',
                    },
                    {
                      labelKey: 'dashboard.chartCancelled' as const,
                      state: SubscriptionState.CANCELLED,
                      color: 'error.main',
                    },
                    {
                      labelKey: 'dashboard.chartExpired' as const,
                      state: SubscriptionState.EXPIRED,
                      color: 'text.secondary',
                    },
                    {
                      labelKey: 'dashboard.chartSuspended' as const,
                      state: SubscriptionState.SUSPENDED,
                      color: 'info.main',
                    },
                  ].map(({ labelKey, state, color }) => (
                    <Chip
                      key={state}
                      label={`${t(labelKey)}: ${subscriptionStateCounts[state] ?? 0}`}
                      size="small"
                      sx={{ [`& .MuiChip-label`]: { color } }}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Paper>

              <Box
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}
              >
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t('tenantDetail.latestInvoices')}
                  </Typography>
                  {documents.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      {t('tenantDetail.noInvoices')}
                    </Typography>
                  ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {documents.slice(0, 5).map((d) => (
                        <li key={d.id}>
                          <Typography variant="body2">
                            {d.number} · {formatCurrency(d.amountGross)} · {formatDate(d.createdAt)}
                          </Typography>
                          <Chip
                            label={translateAccountingDocumentState(t, d.state)}
                            size="small"
                            color={accountingDocumentStateColors[d.state] || 'default'}
                            sx={{ mt: 0.25 }}
                          />
                        </li>
                      ))}
                    </Box>
                  )}
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t('tenantDetail.latestPayments')}
                  </Typography>
                  {payments.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      {t('tenantDetail.noPayments')}
                    </Typography>
                  ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {payments.slice(0, 5).map((p) => (
                        <li key={p.id}>
                          <Typography variant="body2">
                            {formatCurrency(p.amountGross)} ·{' '}
                            {formatDate(p.paymentDate || p.createdAt)}
                          </Typography>
                          <Chip
                            label={translatePaymentState(t, p.state)}
                            size="small"
                            color={paymentStateColors[p.state] || 'default'}
                            sx={{ mt: 0.25 }}
                          />
                        </li>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabIndex} index={1}>
            <DataTable
              enableMobileCards
              columns={subscriptionColumns}
              data={subscriptions.map((s) => ({ ...s, [Symbol.for('key')]: s.id }))}
              loading={subsLoading}
              page={0}
              rowsPerPage={50}
              totalCount={subscriptions.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              onRowClick={(row) =>
                navigate(`/subscriptions/${row.id}`, { state: { fromTenantId: organizationId } })
              }
              paginationMode="client"
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={2}>
            <DataTable
              enableMobileCards
              columns={documentColumns}
              data={documents.map((d) => ({ ...d, [Symbol.for('key')]: d.id }))}
              loading={docsLoading}
              page={0}
              rowsPerPage={50}
              totalCount={documents.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              onRowClick={(row) =>
                navigate(`/invoices/${row.id}`, { state: { fromTenantId: organizationId } })
              }
              paginationMode="client"
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={3}>
            <DataTable
              enableMobileCards
              columns={paymentColumns}
              data={payments.map((p) => ({ ...p, [Symbol.for('key')]: p.id }))}
              loading={paymentsLoading}
              page={0}
              rowsPerPage={50}
              totalCount={payments.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              onRowClick={(row) =>
                navigate(`/payments/${row.id}`, { state: { fromTenantId: organizationId } })
              }
              paginationMode="client"
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={4}>
            <DataTable
              enableMobileCards
              columns={unitColumns}
              data={units.map((u) => ({ ...u, [Symbol.for('key')]: u.id }))}
              loading={false}
              page={0}
              rowsPerPage={50}
              totalCount={units.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              paginationMode="client"
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={5}>
            <DataTable
              enableMobileCards
              columns={licenceColumns}
              data={licences.map((l) => ({ ...l, [Symbol.for('key')]: l.id }))}
              loading={licencesLoading}
              page={0}
              rowsPerPage={50}
              totalCount={licences.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              onRowClick={(row) =>
                navigate(`/licences/${row.id}`, { state: { fromTenantId: organizationId } })
              }
              paginationMode="client"
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('tenantDetail.usersOfTenant')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('tenantDetail.usersHint')}
              </Typography>
              {organization.tenant?.name ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={organization.tenant.name}
                    size="medium"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('common.tenantId')}: {organization.tenantId}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('tenantDetail.noDisplayName')}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('tenantDetail.usersApiHint')}
              </Typography>
            </Paper>
          </TabPanel>

          <TabPanel value={tabIndex} index={7}>
            <DataTable
              enableMobileCards
              columns={paymentAccountColumns}
              data={paymentAccounts.map((pa) => ({ ...pa, [Symbol.for('key')]: pa.id }))}
              loading={payAccLoading}
              page={0}
              rowsPerPage={50}
              totalCount={paymentAccounts.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
              onRowClick={(row) =>
                navigate(`/payment-accounts/${row.id}`, { state: { fromTenantId: organizationId } })
              }
              paginationMode="client"
            />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}
